import { useMemo } from "react";

/* Floating particles that drift across a section */
export const ParticleField = ({ count = 20, className = "" }) => {
  const particles = useMemo(() =>
    Array.from({ length: count }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
      size: `${2 + Math.random() * 4}px`,
      duration: `${6 + Math.random() * 10}s`,
      delay: `${Math.random() * 8}s`,
      dx: `${-100 + Math.random() * 200}px`,
      dy: `${-150 + Math.random() * -100}px`,
    })), [count]);

  return (
    <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}>
      {particles.map((p) => (
        <div
          key={p.id}
          className="particle"
          style={{
            left: p.left,
            top: p.top,
            "--size": p.size,
            "--duration": p.duration,
            "--delay": p.delay,
            "--dx": p.dx,
            "--dy": p.dy,
          }}
        />
      ))}
    </div>
  );
};

/* Vertical data stream lines */
export const DataStream = ({ count = 8, className = "" }) => {
  const lines = useMemo(() =>
    Array.from({ length: count }, (_, i) => ({
      id: i,
      left: `${5 + (i / count) * 90}%`,
      duration: `${4 + Math.random() * 6}s`,
      delay: `${Math.random() * 5}s`,
      height: `${40 + Math.random() * 80}px`,
    })), [count]);

  return (
    <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}>
      {lines.map((l) => (
        <div
          key={l.id}
          className="data-line"
          style={{
            left: l.left,
            height: l.height,
            "--duration": l.duration,
            "--delay": l.delay,
          }}
        />
      ))}
    </div>
  );
};

/* Horizontal flow lines */
export const FlowLines = ({ count = 5, className = "" }) => {
  const lines = useMemo(() =>
    Array.from({ length: count }, (_, i) => ({
      id: i,
      top: `${10 + (i / count) * 80}%`,
      duration: `${6 + Math.random() * 8}s`,
      delay: `${Math.random() * 6}s`,
      width: `${60 + Math.random() * 100}px`,
    })), [count]);

  return (
    <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}>
      {lines.map((l) => (
        <div
          key={l.id}
          className="flow-line-h"
          style={{
            top: l.top,
            width: l.width,
            "--duration": l.duration,
            "--delay": l.delay,
          }}
        />
      ))}
    </div>
  );
};

/* Pulsing node network */
export const NodeNetwork = ({ count = 12, className = "" }) => {
  const nodes = useMemo(() =>
    Array.from({ length: count }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
      size: `${3 + Math.random() * 5}px`,
      speed: `${2 + Math.random() * 4}s`,
      delay: `${Math.random() * 3}s`,
    })), [count]);

  return (
    <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}>
      {nodes.map((n) => (
        <div
          key={n.id}
          className="node"
          style={{
            left: n.left,
            top: n.top,
            "--node-size": n.size,
            "--speed": n.speed,
            "--delay": n.delay,
          }}
        />
      ))}
    </div>
  );
};

/* Morphing gradient blob */
export const MorphBlob = ({ 
  color = "rgba(0,180,216,0.08)", 
  size = "400px",
  className = "" 
}) => (
  <div
    className={`absolute morph-blob pointer-events-none ${className}`}
    style={{
      width: size,
      height: size,
      background: `radial-gradient(ellipse at center, ${color}, transparent 70%)`,
    }}
  />
);

/* Ripple rings emanating from center */
export const RippleRings = ({ count = 3, size = 200, className = "" }) => {
  const rings = useMemo(() =>
    Array.from({ length: count }, (_, i) => ({
      id: i,
      speed: `${3 + i * 1.5}s`,
      delay: `${i * 1}s`,
    })), [count]);

  return (
    <div className={`absolute pointer-events-none ${className}`} style={{ width: size, height: size }}>
      {rings.map((r) => (
        <div
          key={r.id}
          className="ripple-ring absolute inset-0"
          style={{ "--speed": r.speed, "--delay": r.delay }}
        />
      ))}
    </div>
  );
};

/* Orbiting dots around a center point */
export const OrbitDots = ({ count = 3, radius = 120, className = "" }) => {
  const dots = useMemo(() =>
    Array.from({ length: count }, (_, i) => ({
      id: i,
      speed: `${15 + i * 8}s`,
      size: `${4 + Math.random() * 3}px`,
      radius: `${radius + i * 30}px`,
      opacity: 0.6 - i * 0.15,
    })), [count, radius]);

  return (
    <div className={`absolute pointer-events-none ${className}`}>
      {dots.map((d) => (
        <div
          key={d.id}
          className="orbit-dot"
          style={{
            "--orbit-speed": d.speed,
            "--dot-size": d.size,
            "--orbit-radius": d.radius,
            opacity: d.opacity,
          }}
        />
      ))}
    </div>
  );
};
